import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  InputAdornment,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { z } from 'zod';
import TextField from '../components/TextField';
import CustomLink from '../components/CustomLink';
import { API_BASE_URL, USER_ROLES } from '../constants';
import { useRegister } from '../queries/auth';
import { UserContext } from '../context/UserContext';

// const linkedinPattern = /^https:\/\/(www\.)?linkedin\.com\/in\/[A-Za-z0-9_-]+\/?$/;

const schema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .regex(/[A-Z]/, 'Must include at least one uppercase letter')
      .regex(/\d/, 'Must include at least one number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Must include at least one special character'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
    // linkedinUrl: z.string().optional(),
    role: z.enum([USER_ROLES.LEAD_AGENT, USER_ROLES.CLIENT]),
    termsAccepted: z.literal(true, {
      errorMap: () => ({ message: 'You must accept the terms' }),
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
// .superRefine(({ linkedinUrl }, ctx) => {
//   if (linkedinUrl && !linkedinPattern.test(linkedinUrl)) {
//     ctx.addIssue({
//       code: 'custom',
//       message: 'Please enter a valid LinkedIn address.',
//       path: ['linkedinUrl'],
//     });
//   }
// });

const Register = () => {
  const { login } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { mutate: registerUser, isPending: isRegistering } = useRegister();
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: 'onChange',
    shouldFocusError: true,
  });

  const {
    register,
    watch,
    handleSubmit,
    setValue,
  } = methods;

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const error = query.get('error');

    if (error) {
      toast(decodeURIComponent(error), { type: 'error' });
      navigate('/register');
    }
  }, [location, navigate]);

  const onSubmit = (data) => {
    registerUser(
      { data },
      {
        onSuccess: ({ data }) => {
          if (data.token) {
            sessionStorage.removeItem('leadAgentNoticeAccepted');
            localStorage.setItem('authToken', data.token);
          } else {
            toast('Failed to register.', { type: 'error' });
            return;
          }

          if (data.newUser) {
            login(data.newUser);
            toast('Account created successfully!', { type: 'success' });
          } else {
            toast('Failed to register.', { type: 'error' });
          }
        },
        onError: (error) => {
          const message = error.response?.data?.message || error.message;
          toast(message, { type: 'error' });
        },
      },
    );
  };

  const handleSocialLogin = (provider) => {
    window.location.href = `${API_BASE_URL}/auth/${provider}/register/${watch('role')}`;
  };

  const isFormReady = !!watch('role') && !!watch('termsAccepted');

  return (
    <Box height='max-content'>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        minHeight='100%'
        paddingX={{ xs: 1.5, sm: 2 }}
        paddingY={{ xs: 3, sm: 4 }}
      >
        <Typography
          variant='h4'
          gutterBottom
          sx={{
            fontSize: { xs: '24px', sm: '32px', md: '40px' },
            textAlign: 'center',
          }}
        >
          Create your account
        </Typography>
        <Typography
          variant='body1'
          gutterBottom
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            textAlign: 'center',
            mb: { xs: 2, sm: 3 },
          }}
        >
          Have an account?{' '}
          <Button
            color='primary'
            component={CustomLink}
            to='/login'
            sx={{
              textTransform: 'uppercase',
              fontSize: { xs: '14px', sm: '16px' },
            }}
          >
            LOG IN NOW
          </Button>
        </Typography>

        <FormProvider {...methods}>
          <form
            noValidate
            onSubmit={(event) => {
              const form = event.currentTarget;
              ['firstName', 'lastName', 'email', 'password', 'confirmPassword'].forEach((fieldName) => {
                const field = form.elements.namedItem(fieldName);
                const nativeValue = typeof field?.value === 'string' ? field.value : '';
                if (nativeValue && nativeValue !== watch(fieldName)) {
                  setValue(fieldName, nativeValue, { shouldDirty: true, shouldValidate: true });
                }
              });
              return handleSubmit(onSubmit)(event);
            }}
          >
            <FormControl
              component='fieldset'
              sx={{
                marginBottom: { xs: '10px', sm: '12px' },
                width: '100%',
              }}
            >
              <RadioGroup
                row
                sx={{
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                }}
              >
                <Box display='flex' alignItems='center' gap={1} flexWrap='wrap' justifyContent='center' flexGrow={1}>
                  <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>Sign up as</Typography>
                  <FormControlLabel
                    value={USER_ROLES.LEAD_AGENT}
                    control={<Radio {...register('role')} size='small' />}
                    label='Lead Agent'
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '14px', sm: '16px' },
                      },
                    }}
                  />
                  <Tooltip title="If you want to set up meetings for TechXpander's clients.">
                    <Typography
                      variant='caption'
                      sx={{
                        cursor: 'help',
                        marginLeft: '-22px',
                        fontSize: { xs: '12px', sm: '14px' },
                      }}
                    >
                      ?
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>or</Typography>
                  <FormControlLabel
                    value={USER_ROLES.CLIENT}
                    control={<Radio {...register('role')} size='small' />}
                    label='Company'
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '14px', sm: '16px' },
                      },
                    }}
                  />
                  <Tooltip title="If you want TechXpander's network of Lead Agents to set up meetings with decision-makers for you.">
                    <Typography
                      variant='caption'
                      sx={{
                        cursor: 'help',
                        marginLeft: '-22px',
                        fontSize: { xs: '12px', sm: '14px' },
                      }}
                    >
                      ?
                    </Typography>
                  </Tooltip>
                </Box>
              </RadioGroup>
            </FormControl>

            <Box
              display='flex'
              justifyContent='center'
              sx={{
                marginBottom: { xs: '16px', sm: '20px' },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watch('termsAccepted')}
                    onChange={(e) => setValue('termsAccepted', e.target.checked)}
                    color='primary'
                    size='small'
                  />
                }
                label={
                  <Typography variant='body2' color='textPrimary' sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
                    I accept the{' '}
                    <Link
                      to='/content/privacy-policy'
                      style={{ color: '#1976d2', textDecoration: 'underline' }}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Privacy Policy
                    </Link>{' '}
                    and the{' '}
                    <Link
                      to='/content/terms-of-service'
                      style={{ color: '#1976d2', textDecoration: 'underline' }}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Terms of Service
                    </Link>
                  </Typography>
                }
              />
            </Box>

            <Box
              width='100%'
              maxWidth={400}
              mt={2}
              sx={{
                opacity: isFormReady ? 1 : 0.4,
                pointerEvents: isFormReady ? 'auto' : 'none',
              }}
            >
              <Button
                variant='outlined'
                fullWidth
                startIcon={<GoogleIcon />}
                sx={{
                  marginBottom: { xs: '8px', sm: '10px' },
                  color: '#000',
                  fontSize: { xs: '14px', sm: '16px' },
                  py: { xs: 1, sm: 1.5 },
                }}
                onClick={() => handleSocialLogin('google')}
              >
                Google
              </Button>
              <Button
                variant='outlined'
                fullWidth
                startIcon={<FacebookIcon />}
                sx={{
                  marginBottom: { xs: '8px', sm: '10px' },
                  color: '#3b5998',
                  fontSize: { xs: '14px', sm: '16px' },
                  py: { xs: 1, sm: 1.5 },
                }}
                onClick={() => handleSocialLogin('facebook')}
              >
                Facebook / <InstagramIcon sx={{ marginRight: '6px', marginLeft: '6px' }} /> Instagram
              </Button>

              <Box
                display='flex'
                alignItems='center'
                width='100%'
                gap={0.5}
                mt={2}
                sx={{
                  '&::before, &::after': {
                    content: '""',
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'grey.500',
                  },
                }}
              >
                <Typography variant='body2' color='textSecondary' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  Or with email and password
                </Typography>
              </Box>

              <Box mt={3} width='100%' maxWidth={400}>
                <Box display='flex' alignItems='start' gap={1} flexDirection={{ xs: 'column', sm: 'row' }}>
                  <TextField label='First Name' name='firstName' sx={{ mb: 0 }} />
                  <TextField label='Last Name' name='lastName' />
                </Box>
                <TextField
                  label='Email Address'
                  name='email'
                  placeholder='We recommend using your work email'
                  sx={{ mt: { xs: 1.5, sm: 2 } }}
                />
                <TextField
                  label='Password'
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  sx={{ mt: { xs: 1.5, sm: 2 } }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge='end'
                            size='small'
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label='Confirm Password'
                  name='confirmPassword'
                  type={showPassword ? 'text' : 'password'}
                  sx={{ mt: { xs: 1.5, sm: 2 } }}
                />

                <Box width='100%' maxWidth={400} mt={2}>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    fullWidth
                    sx={{
                      py: { xs: 1, sm: 1.5 },
                      fontSize: { xs: '14px', sm: '16px' },
                    }}
                    disabled={!isFormReady || isRegistering}
                  >
                    Register
                  </Button>
                </Box>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
};

export default Register;
